"use client";

import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { db } from "@/lib/firebase";
import { collection, query, limit, getDocs, where } from "firebase/firestore";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";

export interface UserResult {
  id: string;
  displayName: string;
  email?: string;
  profileImageUrl?: string;
  accountType?: string;
}

// Two module-level caches — all users (admin) and public creators (contributor search)
let allUsersCache: UserResult[] | null = null;
let allUsersPromise: Promise<UserResult[]> | null = null;
let creatorsCache: UserResult[] | null = null;
let creatorsPromise: Promise<UserResult[]> | null = null;

function mapDoc(d: any): UserResult {
  const data = d.data();
  return {
    id: d.id,
    // Try every known display-name field — some users have only nickname or firstName
    // Fall back to email prefix so users with no display name still appear in search
    displayName: data.displayName || data.nickname || data.firstName || data.name ||
      (data.email ? data.email.split("@")[0] : "") || "",
    email: data.email || "",
    profileImageUrl: data.profileImageUrl || data.photoURL || data.avatarUrl || "",
    accountType: data.accountType || "",
  };
}

async function loadAllUsers(): Promise<UserResult[]> {
  if (allUsersCache) return allUsersCache;
  if (allUsersPromise) return allUsersPromise;
  allUsersPromise = (async () => {
    try {
      const snap = await getDocs(query(collection(db, "users"), limit(1000)));
      allUsersCache = snap.docs.map(mapDoc).filter(u => u.displayName.trim() !== "");
      return allUsersCache;
    } catch {
      allUsersPromise = null;
      return [];
    }
  })();
  return allUsersPromise;
}

async function loadPublicCreators(): Promise<UserResult[]> {
  if (creatorsCache) return creatorsCache;
  if (creatorsPromise) return creatorsPromise;
  creatorsPromise = (async () => {
    try {
      // Load ALL registered users — no isPublicProfile filter so every user
      // (including those who haven't set a public profile yet) can be found
      // when adding contributors, collaborators, etc.
      const snap = await getDocs(query(collection(db, "users"), limit(1000)));
      const all = snap.docs.map(mapDoc).filter(u => u.displayName.trim() !== "");
      // Sort: creators/admins first, then alphabetically
      creatorsCache = all.sort((a, b) => {
        const aIsCreator = ["creator", "venue", "admin", "superadmin"].includes(a.accountType || "");
        const bIsCreator = ["creator", "venue", "admin", "superadmin"].includes(b.accountType || "");
        if (aIsCreator && !bIsCreator) return -1;
        if (!aIsCreator && bIsCreator) return 1;
        return (a.displayName || "").localeCompare(b.displayName || "");
      });
      return creatorsCache;
    } catch {
      creatorsPromise = null;
      return loadAllUsers();
    }
  })();
  return creatorsPromise;
}

interface UserInputProps {
  id?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onUserSelect?: (user: UserResult) => void;
  placeholder?: string;
  className?: string;
  /** If true, shows all users (admin use). Default: shows public creators only. */
  showAll?: boolean;
}

export function UserInput({
  id,
  value: externalValue,
  defaultValue = "",
  onChange,
  onUserSelect,
  placeholder = "Search users…",
  className,
  showAll = false,
}: UserInputProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<UserResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const value = externalValue !== undefined ? externalValue : internalValue;

  useEffect(() => {
    setIsLoading(true);
    const loader = showAll ? loadAllUsers : loadPublicCreators;
    loader().then((users) => {
      setAllUsers(users);
      setIsLoading(false);
    });
  }, [showAll]);

  const filtered = (() => {
    const term = value.trim().toLowerCase();
    if (!term) return allUsers.slice(0, 20);
    return allUsers
      .filter(
        (u) =>
          u.displayName.toLowerCase().includes(term) ||
          (u.email && u.email.toLowerCase().includes(term)) ||
          // Also match on the portion before the @ in the email (username-style matching)
          (u.email && u.email.split("@")[0].toLowerCase().includes(term))
      )
      .slice(0, 20);
  })();

  const handleChangeValue = (newValue: string) => {
    setInternalValue(newValue);
    onChange?.(newValue);
    setIsOpen(true);
  };

  const handleSelect = (user: UserResult) => {
    setInternalValue(user.displayName);
    onChange?.(user.displayName);
    onUserSelect?.(user);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key === "ArrowDown") { setIsOpen(true); return; }
    if (!isOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((p) => Math.min(p + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((p) => (p > 0 ? p - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && filtered[selectedIndex]) handleSelect(filtered[selectedIndex]);
        else setIsOpen(false);
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showDropdown = isOpen && (allUsers.length > 0 || !isLoading);

  const roleLabel: Record<string, string> = {
    creator:    "Creator",
    venue:      "Venue",
    admin:      "Admin",
    superadmin: "Admin",
  };

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={isLoading ? "Loading…" : placeholder}
        value={value}
        onChange={(e) => handleChangeValue(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={className}
        autoComplete="off"
      />

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          {filtered.length > 0 ? (
            <div className="p-1">
              {filtered.map((user, idx) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm rounded flex items-center gap-2.5 transition-colors",
                    selectedIndex === idx
                      ? "bg-foreground text-background"
                      : "hover:bg-muted text-foreground"
                  )}
                >
                  {user.profileImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={user.profileImageUrl}
                      alt={user.displayName}
                      className="h-7 w-7 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate leading-tight">{user.displayName}</div>
                    {user.email && (
                      <div className="text-[10px] text-muted-foreground truncate">{user.email}</div>
                    )}
                  </div>
                  {user.accountType && roleLabel[user.accountType] && (
                    <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">
                      {roleLabel[user.accountType]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-3 text-sm text-muted-foreground text-center">
              {isLoading
                ? "Loading users…"
                : value.trim()
                ? `No users found for "${value.trim()}". You can still type a name manually.`
                : "No users found."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
